import { useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import {
  UNCOVERED_CLAIM_SORTS,
  type UncoveredClaim,
  type UncoveredClaimSort,
} from "@shared/types/corpus/api.ts";
import { Section } from "../../../../components/primitives/layout/Section";
import {
  DataSequence,
  type DataSequenceColumn,
} from "../../../../components/primitives/data-display/DataSequence";
import { useDataTableSelection } from "../../../../components/primitives/data-display/DataTable/useDataTableSelection";
import { LoadingState } from "../../../../components/primitives/feedback/LoadingState";
import { gaps as spacing } from "../../../../components/primitives/spacing";
import { UNCOVERED_CLAIM_SORT_LABELS } from "../../../../lib/coverageGapView";
import { formatDate } from "../../../../lib/date";
import { formatCount } from "../../../../lib/text";
import { LibraryPage } from "../LibraryPage";
import { ChooseCoverageGapDialog } from "./ChooseCoverageGapDialog";
import { useUncoveredClaims } from "./useUncoveredClaims";

/**
 * The claims we checked and could not answer that are in no coverage gap yet, waiting to be grouped.
 * One claim or several ticked are put in an existing gap, or in a new one made on the spot.
 */
export default function UncoveredClaims() {
  const { filters, setFilters, claims, openGaps, selection, createGap, attachClaims } =
    useUncoveredClaims();
  const [choosing, setChoosing] = useState<string[] | null>(null);

  const ungroupedSelection = useDataTableSelection(selection, {
    rows: claims.items,
    getRowId: (row) => row.claim.id,
  });

  const columns: DataSequenceColumn<UncoveredClaim>[] = [
    {
      key: "claim",
      header: "Claim",
      width: 420,
      cardRole: "primary",
      cell: (row) => row.claim.canonical,
    },
    {
      key: "askers",
      header: "People",
      align: "right",
      cell: (row) => row.demand.askers.toLocaleString(),
    },
    {
      key: "asked",
      header: "Asked",
      align: "right",
      cell: (row) => row.demand.timesAsked.toLocaleString(),
    },
    { key: "last", header: "Last asked", cell: (row) => formatDate(row.demand.lastAskedAt) },
    {
      key: "action",
      header: "",
      cardRole: "action",
      cell: (row) => (
        <Button size="small" onClick={() => setChoosing([row.claim.id])}>
          Add to gap…
        </Button>
      ),
    },
  ];

  return (
    <LibraryPage>
      <Section
        action={
          <Stack direction="row" gap={spacing.snug}>
            <TextField
              select
              size="small"
              label="Rank by"
              value={filters.sort}
              onChange={(e) =>
                setFilters({ ...filters, sort: e.target.value as UncoveredClaimSort })
              }
              sx={{ minWidth: 200 }}
            >
              {UNCOVERED_CLAIM_SORTS.map((sort) => (
                <MenuItem key={sort} value={sort}>
                  {UNCOVERED_CLAIM_SORT_LABELS[sort]}
                </MenuItem>
              ))}
            </TextField>
            {selection.count > 0 ? (
              <Button
                size="small"
                variant="contained"
                onClick={() => setChoosing([...selection.selectedIds])}
              >
                Add {formatCount(selection.count, "claim")} to a gap…
              </Button>
            ) : null}
          </Stack>
        }
      >
        {claims.loading ? (
          <LoadingState />
        ) : claims.error ? (
          <Alert severity="error">{claims.error}</Alert>
        ) : (
          <DataSequence
            columns={columns}
            rows={claims.items}
            getRowId={(row) => row.claim.id}
            selection={ungroupedSelection}
            pagination={claims.pagination}
            emptyMessage="Every claim we could not answer is in a gap."
          />
        )}
      </Section>

      <ChooseCoverageGapDialog
        key={choosing?.join(",") ?? "closed"}
        open={choosing !== null}
        onClose={() => setChoosing(null)}
        title={
          choosing && choosing.length > 1
            ? `Add ${choosing.length} claims to a gap`
            : "Add claim to a gap"
        }
        gaps={openGaps}
        excludeGapId={null}
        onChoose={async (gapId) => {
          if (choosing) await attachClaims(gapId, choosing);
        }}
        onCreate={async (name, note) => {
          if (!choosing) return;
          const gap = await createGap(name, note);
          await attachClaims(gap.id, choosing);
        }}
      />
    </LibraryPage>
  );
}
